import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import ServerEmail from '~/apps/ServerEmail';
import LoadingScene from '~/components/MFEProvider/lib/ReactSuspense/LoadingScene';
import Hosting from '~/scenes/Hosting';
import Overview from '~/scenes/Hosting/scenes/HostingDetail/scenes/Overview';

import FixFileOwnership from './apps/FixFileOwnerShip';
import { getIsDevMode } from './components/DevMode';
import useAMRevamp from './hooks/useAMRevamp';
import HostingList from './Nova/scenes/HostingList';
import SelfManagedHosting from './Nova/scenes/HostingList/scenes/SelfManagedHosting';
import AddSetupSiteFlows from './scenes/AddSetupSiteFlows';

const Resource = lazy(() => import('~/apps/ResourceUsage'));
const SslManager = lazy(() => import('~/apps/SSLManager'));
const Redirects = lazy(() => import('~/apps/Redirects'));
const Backups = lazy(() => import('~/apps/Backups'));
const Databases = lazy(() => import('~/apps/Databases'));
const Sites = lazy(() => import('~/scenes/Sites'));
const SSHKeyManager = lazy(() => import('~/apps/SSHKeyManager'));
const ProServices = lazy(() => import('~/apps/ProServices'));
const CronJobs = lazy(() => import('~/apps/CronJobs'));
const FtpAccessManger = lazy(() => import('~/apps/FTPAccess'));
const Indexes = lazy(() => import('~/apps/Indexes'));
const Emails = lazy(() => import('~/apps/Emails'));

const Resources = lazy(
  () =>
    import(
      './scenes/Hosting/scenes/HostingDetail/scenes/Overview/Cloud/scenes/Resources'
    ),
);
const ChooseDomain = lazy(
  () =>
    import(
      './scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/Server/ChooseDomain'
    ),
);

const PhpInfo = lazy(() => import('~/apps/Phpinfo'));
const Logs = lazy(() => import('~/apps/Logs'));
const DirectoryPrivacy = lazy(() => import('~/apps/DirectoryPrivacy'));
const Apps = lazy(() => import('~/scenes/Apps'));
const AiSites = lazy(() => import('~/apps/AiSites'));
const AiStore = lazy(() => import('~/apps/AiStore'));
const SingleHostingSites = lazy(
  () => import('~/apps/Websites/SingleHostingSites'),
);

const MainRoutes = () => {
  const { isAMRevamp } = useAMRevamp();

  const hostingRoutes = (
    <>
      <Route index element={isAMRevamp ? <HostingList /> : <Overview />} />
      <Route path="ssl-manager/*" element={<SslManager />} />
      <Route path="cron-jobs/*" element={<CronJobs />} />
      <Route path="ssh-keys/*" element={<SSHKeyManager />} />
      <Route path="pro-services/*" element={<ProServices />} />
      <Route path="ftp-access/*" element={<FtpAccessManger />} />
      <Route path="databases/*" element={<Databases />} />
      <Route path="redirects/*" element={<Redirects />} />
      <Route path="email/*" element={<ServerEmail />} />
      <Route path="resources/*" element={<Resources />} />
      <Route path="backups/*" element={<Backups />} />
      <Route path="phpinfo/:siteID/*" element={<PhpInfo />} />

      <Route path="logs/*" element={<Logs />} />
      <Route path="directory-privacy/*" element={<DirectoryPrivacy />} />
      <Route path="indexes/*" element={<Indexes />} />
      <Route path="resource-usage/*" element={<Resource />} />
      <Route path="mail/*" element={<Emails />} />
      <Route path="ownership/*" element={<FixFileOwnership />} />

      <Route path="self-managed-hosting/*" element={<SelfManagedHosting />} />
    </>
  );

  return (
    <Suspense fallback={<LoadingScene />}>
      <Routes>
        {/* Root path without hostingId */}
        <Route path="/" element={<Hosting />}>
          {hostingRoutes}
        </Route>

        {/* Routes with hostingId */}
        <Route path="/:hostingId" element={<Hosting />}>
          {hostingRoutes}
        </Route>

        <Route path="/:hostingId?/sites/*" element={<Sites />} />
        <Route
          path="/:hostingId?/package/websites/*"
          element={<SingleHostingSites />}
        />
        <Route
          path="/:hostingId?/package/websites/add/*"
          element={<AddSetupSiteFlows />}
        />
        {getIsDevMode(['development', 'beta']) && (
          <Route path="/hosting/*">
            <Route index element={<HostingList />} />
            <Route path=":hostingId" element={<Hosting />}>
              <Route index element={<Overview />} />
              {/* <Route path="mail/*" element={<Emails />} /> */}
            </Route>
          </Route>
        )}

        <Route path="/setup-server/*" element={<ChooseDomain />} />

        {/* Apps-Deploy – GitHub-connected deploy flow */}
        <Route path="/apps-deploy/*" element={<Apps />} />

        <Route path="/ai-sites/*" element={<AiSites />} />
        <Route path="/ai-store/*" element={<AiStore />} />

        {/* No validation needed */}
        <Route path="*" element={<LoadingScene />} />
      </Routes>
    </Suspense>
  );
};

export default MainRoutes;
